import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { I18nProvider } from './i18n';
/* Brand faces, bundled rather than fetched.
   
   These were never loaded at all: the CSS named 'Petit Formal Script' and
   'Noto Kufi Arabic' and a comment claimed they came from Google Fonts in
   index.html, but no link ever existed. Every wordmark fell through to whatever
   the OS substitutes for `cursive` — Snell Roundhand on iOS, Dancing Script on
   Android — so the logo rendered in a different typeface on each phone, and in
   the correct one only on the website.

   Bundled, not linked to a CDN: the app runs from local files inside a webview,
   so a remote stylesheet would put a network round-trip in front of the sign-in
   screen and fail outright offline. */
import '@fontsource/petit-formal-script/400.css';
/* Arabic subset only — the Latin glyphs in this family would never be used
   (Latin text is set in the UI sans) and were half the bundled weight. */
import '@fontsource/noto-naskh-arabic/arabic-400.css';
import '@fontsource/noto-naskh-arabic/arabic-500.css';
import '@fontsource/noto-naskh-arabic/arabic-700.css';

import './styles/global.css';
import './styles/rtl.css';

/**
 * Recover from a stale app shell after a deploy.
 *
 * Screens are loaded as separate chunks. When a new version ships, the chunk
 * filenames change, and a tab still holding the previous index.html asks for
 * files that no longer exist — the request falls through to the SPA's HTML,
 * which is not a JavaScript module, and the screen renders blank. The service
 * worker now claims clients immediately, but a tab open across the deploy can
 * still be caught mid-flight.
 *
 * Vite fires this event exactly then. Reload once to pick up the new shell.
 * The sessionStorage guard matters: if the reload does not fix it (a genuinely
 * broken deploy, say) we must not trap a mother in a refresh loop — better one
 * visible error than an app that never settles.
 */
/**
 * Mark the document when this is running inside the native app.
 *
 * The same build serves three places: the browser PWA, the iOS webview and the
 * Android webview. A few things should only be true of the native shells — most
 * immediately, scrollbars, which Android's WebView paints as a persistent strip
 * down the edge where a real app shows nothing.
 *
 * A `(pointer: coarse)` media query was the obvious way to express that and did
 * not work: it matches in a mobile browser but not in the Android WebView on
 * this hardware, so the strip survived on the phone while looking fixed
 * everywhere else. Capacitor injects this global into the native webview only,
 * which states the condition directly instead of inferring it from input
 * hardware. The frontend deliberately does not depend on @capacitor/core — this
 * build must keep working as a plain web app — so the global is read defensively.
 */
declare global {
  interface Window {
    Capacitor?: { isNativePlatform?: () => boolean; platform?: string };
  }
}

if (window.Capacitor?.isNativePlatform?.() ?? (window.Capacitor?.platform ?? 'web') !== 'web') {
  document.documentElement.classList.add('native-app');
}

/**
 * Service worker: browser only.
 *
 * Inside the native app the assets are already local files, so a worker that
 * precaches them adds no offline benefit — only the risk that it keeps serving
 * the last build. That is not hypothetical: a stylesheet fix was installed on a
 * phone three times over and never appeared, because the worker kept handing
 * back the previous index.html, which referenced the previous hashed CSS.
 *
 * Any worker left over from a build that did register one is torn down here,
 * along with its caches — otherwise the phones that already have it would stay
 * stuck no matter what ships next.
 */
const isNative =
  window.Capacitor?.isNativePlatform?.() ?? (window.Capacitor?.platform ?? 'web') !== 'web';

if (isNative) {
  void navigator.serviceWorker?.getRegistrations?.()
    .then(rs => Promise.all(rs.map(r => r.unregister())))
    .catch(() => { /* nothing registered, or the API is unavailable */ });
  void caches?.keys?.()
    .then(keys => Promise.all(keys.map(k => caches.delete(k))))
    .catch(() => { /* no cache storage to clear */ });
} else {
  void import('virtual:pwa-register')
    .then(({ registerSW }) => registerSW({ immediate: true }))
    .catch(() => { /* the browser build can run without it */ });
}

window.addEventListener('vite:preloadError', () => {
  if (sessionStorage.getItem('reloadedForStaleChunk')) return;
  sessionStorage.setItem('reloadedForStaleChunk', '1');
  window.location.reload();
});

// A clean load means the shell is current; clear the guard so a future deploy
// gets its own single retry.
window.addEventListener('load', () => sessionStorage.removeItem('reloadedForStaleChunk'));

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <I18nProvider>
      <App />
    </I18nProvider>
  </React.StrictMode>
);
